package dev.lingvo.site.components.studio.preview

import dev.lingvo.site.components.studio.canvas.BitmapImage
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.cancel
import org.w3c.files.Blob

/** Shares each authenticated image decode across mounted cells and retires it with the selected language. */
class PreviewBitmapStore {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val requests = mutableMapOf<String, Deferred<BitmapImage>>()
    private val ready = mutableMapOf<String, BitmapImage>()
    private var closed = false

    /** Concurrent cells share one request; failures are retryable instead of poisoning the image cache. */
    suspend fun load(key: String, width: Int? = null, height: Int? = null, fetch: suspend () -> Blob): BitmapImage {
        check(!closed) { "This preview is no longer mounted." }
        val request = requests.getOrPut(key) {
            scope.async {
                val bitmap = previewBitmap(fetch(), key, width, height)
                if (closed) { bitmap.close(); error("This preview is no longer mounted.") }
                ready[key] = bitmap
                bitmap
            }
        }
        return try { request.await() } catch (failure: Throwable) {
            if (request.isCancelled && requests[key] === request) requests.remove(key)
            throw failure
        }
    }

    /** Cancels pending fetches and closes bitmaps even if a cell's rendering coroutine has already left. */
    fun close() {
        closed = true
        scope.cancel()
        ready.values.forEach(BitmapImage::close)
        ready.clear()
        requests.clear()
    }
}
